import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';

/**
 * A router, in about a hundred lines, because the alternative costs more than it
 * is worth here.
 *
 * The perf budget in 03 is real and this surface has exactly twelve flat routes,
 * one dynamic segment each at most, no nested layouts, no data loaders and no
 * code-split boundaries worth the round trip. A routing library would be one of
 * the largest things in the bundle and would earn none of it.
 *
 * What it does support, because the screens need it: History API navigation,
 * back and forward, `:params`, query strings, and replace-vs-push (an entry
 * screen that resolves into the menu must **replace**, or the back gesture puts
 * the guest on a spinner that re-opens a session).
 *
 * If this ever needs nested routes or route-level data loading, replace it —
 * nothing outside this file knows how it works.
 */

export interface RouteMatch {
  /** The matched pattern, e.g. `/menu/:itemId`. Null when nothing matched. */
  pattern: string | null;
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

export interface RouterValue extends RouteMatch {
  navigate: (to: string, options?: {replace?: boolean}) => void;
  back: () => void;
}

const RouterContext = createContext<RouterValue | null>(null);

/** Split a location into path and query without needing an origin. */
function readLocation(): {path: string; search: string} {
  const {pathname, search} = globalThis.location;
  return {path: pathname || '/', search};
}

/**
 * Match one pattern against one path.
 *
 * Segment-by-segment rather than a regular expression, so a token containing a
 * slash-encoded character cannot smuggle its way into a different route.
 */
export function matchPath(
  pattern: string,
  path: string,
): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = path.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;

  const params: Record<string, string> = {};
  for (let i = 0; i < patternParts.length; i += 1) {
    const p = patternParts[i]!;
    const value = pathParts[i]!;
    if (p.startsWith(':')) {
      params[p.slice(1)] = decodeURIComponent(value);
    } else if (p !== value) {
      return null;
    }
  }
  return params;
}

/** First pattern that matches, in the order given. */
export function resolveRoute(patterns: readonly string[], path: string): RouteMatch {
  for (const pattern of patterns) {
    const params = matchPath(pattern, path);
    if (params) return {pattern, path, params, query: new URLSearchParams()};
  }
  return {pattern: null, path, params: {}, query: new URLSearchParams()};
}

export function RouterProvider({
  patterns,
  children,
}: {
  /** Every route pattern the app knows, most specific first. */
  patterns: readonly string[];
  children: ReactNode;
}) {
  const [location, setLocation] = useState(readLocation);

  useEffect(() => {
    const onPopState = () => setLocation(readLocation());
    globalThis.addEventListener('popstate', onPopState);
    return () => globalThis.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((to: string, options?: {replace?: boolean}) => {
    if (options?.replace) globalThis.history.replaceState(null, '', to);
    else globalThis.history.pushState(null, '', to);
    setLocation(readLocation());
    // A new screen starts at the top. Without this, opening an item from
    // halfway down a long menu lands mid-page on the detail screen.
    globalThis.scrollTo?.(0, 0);
  }, []);

  const value = useMemo<RouterValue>(() => {
    const match = resolveRoute(patterns, location.path);
    return {
      ...match,
      query: new URLSearchParams(location.search),
      navigate,
      back: () => globalThis.history.back(),
    };
  }, [patterns, location.path, location.search, navigate]);

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter must be used inside <RouterProvider>');
  return value;
}

/**
 * An in-app link.
 *
 * It is a real `<a href>` — a guest long-pressing to open the menu in a new tab
 * should get a working page, and a screen reader should hear a link. The click
 * handler only takes over the plain-left-click case.
 */
export function Link({
  to,
  replace,
  onClick,
  children,
  ...rest
}: {
  to: string;
  replace?: boolean;
  children: ReactNode;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>) {
  const {navigate} = useRouter();

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(to, {replace});
  };

  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
